import React from 'react'
import { Layout } from '../components/layout/Layout'
import { SectionHeading } from '../components/ui/SectionHeading'
import { Button } from '../components/ui/Button'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  Youtube,
  Facebook,
  BookOpen,
  Headphones,
  Video,
  ExternalLink,
  Play,
  Calendar,
  CheckCircle,
  ArrowRight,
  ShoppingBag,
  User,
  Clock,
} from 'lucide-react'
const sermons = [
  {
    id: 1,
    title: 'MOTHERS DAY CELEBRATION 1B',
    speaker: 'Pastor Chris & Stella',
    date: 'May 10, 2026',
    duration: '1 hr 15 min',
    thumbnail: 'https://img.youtube.com/vi/36hHSo46Gww/maxresdefault.jpg',
    youtubeId: '36hHSo46Gww',
  },
  {
    id: 2,
    title: 'MOTHERS DAY CELEBRATION 1A',
    speaker: 'Pastor Chris & Stella',
    date: 'May 10, 2026',
    duration: '52 min',
    thumbnail: 'https://img.youtube.com/vi/A5yiv-TYY6Y/maxresdefault.jpg',
    youtubeId: 'A5yiv-TYY6Y',
  },
  {
    id: 3,
    title: 'Opening Gates - #ImpactWednesday',
    speaker: 'Pastor Chris',
    date: 'May 6, 2026',
    duration: '48 min',
    thumbnail: 'https://img.youtube.com/vi/V7saiJKGEQE/maxresdefault.jpg',
    youtubeId: 'V7saiJKGEQE',
  },
]
const platforms = [
  {
    icon: Youtube,
    title: 'Sermon Archive',
    description: 'Watch past sermons and teachings from our services',
    link: 'https://youtube.com/@householdchurch',
    linkText: 'Watch on YouTube',
    color: 'bg-red-500',
  },
  {
    icon: Facebook,
    title: 'Facebook Live',
    description: 'Join our live services and community updates',
    link: 'https://facebook.com/householdchurch',
    linkText: 'Follow on Facebook',
    color: 'bg-blue-600',
  },
  {
    icon: BookOpen,
    title: 'Deep Dive Materials',
    description: 'Weekly study guides and devotional resources for Deep Dive',
    link: '/deep-dive',
    linkText: 'Access Materials',
    color: 'bg-[#0A1F44]',
    internal: true,
  },
  {
    icon: ShoppingBag,
    title: 'Online Store',
    description: 'Ministry resources, books, and branded apparel.',
    link: '/store',
    linkText: 'Visit Store',
    color: 'bg-[#fc0719]',
    internal: true,
  },
]
export function ResourcesPage() {
  return (
    <Layout>
      {/* Hero Section */}
      <div className="bg-[#0A1F44] pt-44 md:pt-48 pb-24 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://images.unsplash.com/photo-1516450360452-9312f5e86fc7?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80"
            alt="Background"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A1F44]/60 via-transparent to-[#0A1F44]" />

        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <span className="inline-block text-[#fc0719] text-sm font-bold tracking-[0.3em] uppercase mb-4">
              Resources & Media
            </span>
            <h1 className="text-5xl md:text-7xl font-serif font-bold mb-6 tracking-tight">
              Equipping <span className="text-[#fc0719]">You</span>
            </h1>
            <p className="text-xl text-white/80 leading-relaxed max-w-2xl">
              Access our library of sermons, study materials, and ministry tools
              designed to fuel your spiritual growth and walk with Christ.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Platforms Grid */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <SectionHeading
            title="Ways to Connect"
            subtitle="Our Platforms"
            alignment="center"
          />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mt-12">
            {platforms.map((platform, index) => (
              <motion.div
                key={platform.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-[#F5F7FA] p-8 rounded-2xl border border-gray-100 hover:bg-white hover:shadow-xl hover:border-[#fc0719]/20 transition-all duration-300 group"
              >
                <div
                  className={`w-14 h-14 ${platform.color} rounded-xl flex items-center justify-center mb-6 text-white group-hover:scale-110 transition-transform shadow-lg`}
                >
                  <platform.icon className="w-7 h-7" />
                </div>
                <h3 className="text-xl font-bold text-[#0A1F44] mb-3 font-serif">
                  {platform.title}
                </h3>
                <p className="text-gray-600 mb-8 text-sm leading-relaxed">
                  {platform.description}
                </p>
                <Link
                  to={platform.link}
                  className="inline-flex items-center text-sm font-bold text-[#0A1F44] hover:text-[#fc0719] transition-colors group/link"
                >
                  {platform.linkText}
                  <ArrowRight className="w-4 h-4 ml-2 group-hover/link:translate-x-1 transition-transform" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Recent Sermons */}
      <section className="py-24 bg-[#F5F7FA]">
        <div className="container-custom">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6">
            <SectionHeading
              title="Latest Messages"
              subtitle="Sermon Archive"
              alignment="left"
              className="mb-0"
            />
            <a
              href="https://youtube.com/@householdchurch"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button
                variant="outline"
                className="border-[#0A1F44] text-[#0A1F44] hover:bg-[#0A1F44] hover:text-white"
                rightIcon={<Youtube className="w-4 h-4" />}
              >
                View All on YouTube
              </Button>
            </a>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {sermons.map((sermon, index) => (
              <motion.div
                key={sermon.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-2xl transition-all duration-500 group"
              >
                <div className="relative aspect-video overflow-hidden">
                  <img
                    src={sermon.thumbnail}
                    alt={sermon.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-black/20 group-hover:bg-black/40 transition-colors" />
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="w-16 h-16 bg-[#fc0719] rounded-full flex items-center justify-center text-white shadow-xl scale-90 group-hover:scale-100 transition-transform duration-300">
                      <Play className="w-6 h-6 fill-current ml-1" />
                    </div>
                  </div>
                  <div className="absolute bottom-4 left-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded text-[10px] font-bold text-[#0A1F44] uppercase tracking-widest">
                    {sermon.duration}
                  </div>
                </div>
                <div className="p-8">
                  <div className="flex items-center gap-4 text-xs font-bold text-[#fc0719] uppercase tracking-widest mb-4">
                    <span className="flex items-center gap-1.5">
                      <Calendar className="w-3.5 h-3.5" />
                      {sermon.date}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold text-[#0A1F44] mb-4 font-serif leading-tight group-hover:text-[#fc0719] transition-colors">
                    {sermon.title}
                  </h3>
                  <div className="flex items-center gap-2 text-gray-500 text-sm mb-8">
                    <User className="w-4 h-4 text-gray-400" />
                    <span>{sermon.speaker}</span>
                  </div>
                  <div className="pt-6 border-t border-gray-100">
                    <a
                      href={`https://youtube.com/watch?v=${sermon.youtubeId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-xs font-bold text-[#0A1F44] uppercase tracking-[0.2em] hover:text-[#fc0719] transition-colors"
                    >
                      Watch Message
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </a>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Study Section - Redesigned to White */}
      <section className="py-24 bg-white overflow-hidden">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative order-2 lg:order-1"
            >
              <div className="relative rounded-3xl overflow-hidden shadow-2xl bg-gray-100 group">
                <div className="aspect-video">
                  <iframe
                    src="https://www.youtube.com/embed/36hHSo46Gww"
                    title="Featured Sermon"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    className="w-full h-full"
                  />
                </div>
                <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-[#fc0719] rounded-full -z-10 blur-3xl opacity-20" />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-1 lg:order-2"
            >
              <span className="text-[#fc0719] text-sm font-bold tracking-[0.3em] uppercase mb-4 block">
                Featured Study
              </span>
              <h2 className="text-3xl md:text-5xl font-serif font-bold text-[#0A1F44] mb-8 leading-tight">
                Deep Dive: <br />
                <span className="text-[#fc0719]">Understanding</span> the
                Kingdom
              </h2>
              <p className="text-gray-600 text-lg leading-relaxed mb-10">
                Join us every Sunday at 9:00 AM for our Deep Dive Bible study.
                This interactive session is designed to help you understand
                God's Word on a deeper level and apply it to your daily life.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-10">
                {[
                  'In-depth scriptural analysis',
                  'Interactive Q&A sessions',
                  'Practical life applications',
                  'Weekly study materials',
                ].map((item) => (
                  <div
                    key={item}
                    className="flex items-center gap-3 text-gray-700 font-medium"
                  >
                    <div className="w-6 h-6 bg-[#fc0719]/10 rounded-full flex items-center justify-center shrink-0">
                      <CheckCircle className="w-3.5 h-3.5 text-[#fc0719]" />
                    </div>
                    <span className="text-sm">{item}</span>
                  </div>
                ))}
              </div>

              <Link to="/deep-dive">
                <Button
                  variant="primary"
                  size="lg"
                  className="px-10 shadow-xl shadow-red-600/20"
                >
                  Explore Deep Dive
                </Button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Social Media Section - Redesigned to Light Gray */}
      <section className="py-24 bg-[#F5F7FA]">
        <div className="container-custom text-center">
          <SectionHeading
            title="Stay Updated"
            subtitle="Follow Us"
            alignment="center"
          />
          <p className="text-gray-600 mb-12 max-w-xl mx-auto text-lg">
            Never miss a moment. Join our online community for daily
            inspiration, live service notifications, and church updates.
          </p>
          <div className="flex flex-wrap justify-center gap-6">
            <a
              href="https://youtube.com/@householdchurch"
              target="_blank"
              rel="noopener noreferrer"
              className="group"
            >
              <Button
                variant="white"
                size="lg"
                className="shadow-md hover:shadow-xl transition-all px-10 border border-gray-100"
                leftIcon={<Youtube className="w-5 h-5 text-red-600" />}
              >
                YouTube
              </Button>
            </a>
            <a
              href="https://facebook.com/householdchurch"
              target="_blank"
              rel="noopener noreferrer"
              className="group"
            >
              <Button
                variant="white"
                size="lg"
                className="shadow-md hover:shadow-xl transition-all px-10 border border-gray-100"
                leftIcon={<Facebook className="w-5 h-5 text-blue-600" />}
              >
                Facebook
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Newsletter Section - Refined Navy Footer-like Section */}
      <section className="py-24 bg-[#0A1F44] relative overflow-hidden">
        {/* Background Accents */}
        <div className="absolute top-0 left-0 w-full h-full">
          <div className="absolute top-[-10%] right-[-10%] w-[40%] h-[60%] bg-[#fc0719]/10 blur-[120px] rounded-full" />
          <div className="absolute bottom-[-10%] left-[-10%] w-[40%] h-[60%] bg-blue-500/5 blur-[120px] rounded-full" />
        </div>

        <div className="container-custom relative z-10">
          <div className="max-w-4xl mx-auto bg-white/5 backdrop-blur-md border border-white/10 rounded-[2.5rem] p-10 md:p-16 text-center">
            <span className="text-[#fc0719] text-sm font-bold tracking-[0.4em] uppercase mb-6 block">
              The Household Weekly
            </span>
            <h2 className="text-3xl md:text-5xl font-serif font-bold text-white mb-6 leading-tight">
              Spiritual Growth <br />
              <span className="text-[#fc0719]">Delivered</span> to Your Inbox
            </h2>
            <p className="text-white/70 text-lg leading-relaxed mb-12 max-w-2xl mx-auto">
              Get weekly sermon notes, study guides, and exclusive ministry
              updates to keep you connected and growing throughout the week.
            </p>

            <form
              className="flex flex-col sm:flex-row gap-4 justify-center max-w-2xl mx-auto"
              onSubmit={(e) => e.preventDefault()}
            >
              <input
                type="email"
                required
                placeholder="Enter your email address"
                className="h-16 px-8 rounded-xl bg-white/10 border border-white/20 text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-[#fc0719] focus:bg-white/15 transition-all w-full sm:flex-1 text-lg"
              />
              <Button
                type="submit"
                size="lg"
                className="h-16 bg-[#fc0719] hover:bg-[#d4061a] border-none px-10 text-lg font-bold tracking-widest shadow-2xl shadow-red-600/40"
              >
                SUBSCRIBE
              </Button>
            </form>

            <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6">
              {[
                { label: 'Sermon Notes', icon: BookOpen },
                { label: 'Study Guides', icon: Headphones },
                { label: 'Event Alerts', icon: Calendar },
                { label: 'Store Deals', icon: ShoppingBag },
              ].map((item) => (
                <div
                  key={item.label}
                  className="flex flex-col items-center gap-2"
                >
                  <item.icon className="w-5 h-5 text-[#fc0719]/80" />
                  <span className="text-white/50 text-[10px] font-bold uppercase tracking-widest">
                    {item.label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </Layout>
  )
}
function CheckCircle({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
      <polyline points="22 4 12 14.01 9 11.01" />
    </svg>
  )
}
